"use client";

import { useOptimistic } from "react";
import {
  type Cart as CartData,
  CartType,
} from "@cappelendamm/e-commerce-services";

export type OptimisticAction =
  | { type: "INCREASE"; sku: string; quantity?: number }
  | { type: "DECREASE"; sku: string; quantity?: number }
  | { type: "SET_QUANTITY"; sku: string; quantity: number }
  | { type: "CLEAR" }
  | { type: "SWAP"; targetCartType: CartType };

export function cartReducer(
  state: CartData,
  action: OptimisticAction,
): CartData {
  switch (action.type) {
    case "INCREASE": {
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.variant.sku !== action.sku) return item;
          return { ...item, quantity: item.quantity + (action.quantity ?? 1) };
        }),
      };
    }

    case "DECREASE": {
      const quantity = action.quantity ?? 1;
      const targetItem = state.items.find(
        (item) => item.variant.sku === action.sku,
      );
      if (!targetItem) return state;

      const newQuantity = targetItem.quantity - quantity;
      if (newQuantity <= 0) {
        return {
          ...state,
          items: state.items.filter((item) => item.variant.sku !== action.sku),
        };
      }

      return {
        ...state,
        items: state.items.map((item) => {
          if (item.variant.sku !== action.sku) return item;
          return { ...item, quantity: newQuantity };
        }),
      };
    }

    case "SET_QUANTITY": {
      if (action.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter((item) => item.variant.sku !== action.sku),
        };
      }

      return {
        ...state,
        items: state.items.map((item) => {
          if (item.variant.sku !== action.sku) return item;
          return { ...item, quantity: action.quantity };
        }),
      };
    }

    case "CLEAR": {
      return {
        ...state,
        items: [],
        total: { ...state.total, gross: 0, net: 0, taxAmount: 0 },
      };
    }

    case "SWAP": {
      return {
        ...state,
        items: [],
        total: { ...state.total, gross: 0, net: 0, taxAmount: 0 },
        cartType: action.targetCartType,
      };
    }

    default:
      return state;
  }
}

/**
 * Wraps cart state with React's useOptimistic for instant UI feedback.
 * The optimistic state automatically reverts to `confirmedCart` when it updates.
 */
export function useOptimisticCart(confirmedCart: CartData) {
  const [optimisticCart, applyOptimistic] = useOptimistic<
    CartData,
    OptimisticAction
  >(confirmedCart, cartReducer);

  return { optimisticCart, applyOptimistic };
}
